import "../../styles/field.css";
import "./index.css";
import { useCallback, useEffect, useId, useMemo, useRef } from "react";
import { useControlledValue } from "../../hooks/useControlledValue";
import { Dropdown } from "../Dropdown";
import { SelectProvider } from "./context";
import { SelectContent } from "./SelectContent";
import { SelectTrigger } from "./SelectTrigger";
import type { ISelectContext, ISelectProps } from "./types";

export const Select = (props: ISelectProps) => {
  const {
    open: controlledOpen,
    onOpenChange: controlledOnOpenChange,
    options,
    loading,
    disabled,
    multiple,
    value,
    onChange,
    onClear,
    formatDisplayValue,
    minWidth,
    maxWidth,
    minHeight,
    maxHeight = 260,
    matchTriggerWidth = true,
    placement = "bottom-start",
    id,
    ariaLabel,
    ariaLabelledby,
    ariaDescribedby,
    preventDefaultOnTrigger,
    stopPropagationOnTrigger,
  } = props;

  const { value: open, onChange: onOpenChange } = useControlledValue<boolean>({
    controlledValue: controlledOpen,
    controlledOnChange: controlledOnOpenChange,
    defaultValue: false,
  });

  const generatedId = useId();
  const baseId = id || generatedId;
  const triggerId = `${baseId}-trigger`;
  const contentId = `${baseId}-content`;

  const typeaheadListRef = useRef<(string | null)[]>([]);
  useEffect(() => {
    typeaheadListRef.current = options.map((opt) => opt.text ?? null);
  }, [options]);

  const selectedOptions = useMemo(() => {
    if (!value) {
      return [];
    }

    const valueArray = Array.isArray(value) ? value : [value];
    return options.filter((opt) => valueArray.includes(opt.id));
  }, [value, options]);

  const displayValue = useMemo(() => {
    if (selectedOptions.length === 0) {
      return "";
    }

    const text = selectedOptions.map((opt) => opt.text).join(", ");

    if (formatDisplayValue) {
      return formatDisplayValue(text);
    }

    return text;
  }, [selectedOptions, formatDisplayValue]);

  const handleSelect = useCallback(
    (optionId: string) => {
      const clickedOption = options.find((opt) => opt.id === optionId);

      if (multiple) {
        const currentValue = Array.isArray(value) ? value : [];
        const updatedValue = currentValue.includes(optionId)
          ? currentValue.filter((v) => v !== optionId)
          : [...currentValue, optionId];
        onChange(updatedValue, clickedOption);
      } else {
        onChange(optionId, clickedOption);
        onOpenChange(false);
      }
    },
    [multiple, value, onChange, onOpenChange, options],
  );

  const hasValue = multiple
    ? Array.isArray(value) && value.length > 0
    : value !== undefined && value !== "";

  const showClearButton = !disabled && !loading && !!onClear && hasValue;

  const contextValue: ISelectContext = {
    ...props,
    displayValue,
    hasValue,
    showClearButton,
    onSelect: handleSelect,
    contentId,
    maxHeight,
  };

  return (
    <SelectProvider value={contextValue}>
      <Dropdown
        trigger={<SelectTrigger />}
        content={<SelectContent />}
        triggerId={triggerId}
        contentId={contentId}
        open={open}
        onOpenChange={onOpenChange}
        disabled={disabled}
        interactions={["click", "dismiss", "listNavigation", "typeahead"]}
        role="listbox"
        innerControl
        ariaLabel={ariaLabel}
        ariaLabelledby={ariaLabelledby}
        ariaDescribedby={ariaDescribedby}
        placement={placement}
        offset={8}
        floatingFocusManager={{
          restoreFocus: false,
          initialFocus: 0,
        }}
        matchTriggerWidth={matchTriggerWidth}
        minWidth={minWidth}
        maxWidth={maxWidth}
        minHeight={minHeight}
        maxHeight={maxHeight}
        typeaheadListRef={typeaheadListRef}
        preventDefaultOnTrigger={preventDefaultOnTrigger}
        stopPropagationOnTrigger={stopPropagationOnTrigger}
      />
    </SelectProvider>
  );
};
